import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "./../redux-hooks";
import { GeneralV3Response, InterceptorOption, LoadingType } from "../../../types/CommonTypes";
import { submitPractice, fetchAllProducts, completeRegistration } from "../../../service/PracticeService";
import { PracticeLocationInfo } from "./practiceLocationSlice";
import { PracticeProviderInfo } from "./practiceProvidersSlice";
import { ProductListInfo } from "../product/productListSlice";
import { AuthorizedSignerInfo } from "../../../components/operation/practice/AuthorizedSignerPopup";
import { providerAuthorizedUserInfo } from "./practiceEditSlice";

export type PracticeInfo = {
  practiceId: string;
  practiceName: string;
  contactFirstName: string;
  contactLastName: string;
  contactMiddleName: string;
  contactEMail: string;
  contactEmail?: string;//CS-1406 
  approvalStatus?: string; //CS-1406
  associateAgreementDocumentId?: number;//CS-1406
  contactPhone: string;
  contactFax: string;
  apFirstName: string;
  apMiddleName: string;
  apLastName: string;
  apPhone: string;
  apEmail: string;
  ptan: string;
  npi: string;
  /** CS-2075 — per-pharmacy billing codes, keyed by static pharmacy key. */
  practicePharmacyConfig?: Record<string, { billing_code?: string | number }>;
  taxId: string;
  billingType: string;
  // notificationEmails: string;
  invitationCode: string;
  practiceRequestInfo : {
    billerFirstName: string;
    billerMiddleName: string;
    billerLastName: string;
    billerContactPhone: string;
    billerContactEmail: string;
    // billerStreetAddress: string;
    // billerUnitNumber: string;
    // billerCity: string;
    // billerState: string;
    // billerZipCode: string;
    // billerNotApplicable: boolean;
    dmeLicense:boolean| string | null;
    billingCollagen: boolean|string | null;
    billMedicare: boolean |string| null;
    billMedicareAdvantage: boolean| string | null;
    billCommercial: boolean | string | null;
    repOrDistributorName: string,
    anyQuestions: string,
    // woundCareFocus: '',
    //CS-1242 - Start
    woundCareFocus: string | null,
    //CS-1242 - End
    //CS-1248 - Start
    interestedProductCategory:string[]|null;
    //CS-1248 - End
    averageWoundsTreatedPerMonth: string;
    placesOfService: string[];
    // insurancePayer1: string;
    // insurancePayer2: string;
    // insurancePayer3: string;
    customerSignature : string;
    // customerFirstName : string;
    // customerMiddleName: string;
    // customerLastName: string;
    customerName: string;
    authorizedSigners: AuthorizedSignerInfo[];
  }
  practiceLocations: PracticeLocationInfo[];
  practiceProviders: PracticeProviderInfo[];
  agencyId?: number;
  practiceTypeCode?: string|null;
  //CS-1243 - start
  isEMREnabled?: boolean; 
  emrIntegrationProvider?: string;
  emrProviderPracticeId?: string;
  //CS-1243 - end
  //CS-1242 - start
  specialization: string | null,
  claimDueDays: number | null,
  isClaimSupported?: boolean,
  isAutoIVREnabled?: boolean;
  allowFreeShipping?: boolean; // CS-1997 - practice-level free shipping override (P&B only)
  authorizedUserList: providerAuthorizedUserInfo[];
  //CS-1242 - end
  dosPreference: string | null,
  dosChangeReason?: string | null,
};

type PracticeRegistrationState = {
  practiceRegistrationInfo?: PracticeInfo | null;
  submitPracticeDataFetchStatus?: LoadingType;
  productList: ProductListInfo[];
  productsListFetchStatus?: LoadingType;
  practiceRegistrationError?: string[];
};

const initialState: PracticeRegistrationState = {
  practiceRegistrationInfo: undefined,
  submitPracticeDataFetchStatus: "idle",
  productList: [],
  productsListFetchStatus: "idle",
  practiceRegistrationError: undefined
};

export const submitPracticeData = createAsyncThunk<any, { practiceData: PracticeInfo | null , interceptorOption: InterceptorOption }>("submitPracticeData", async (data, { rejectWithValue }) => {
  const { practiceData, interceptorOption } = data
  try{
   const response = await submitPractice(practiceData, interceptorOption);
   return response.data;
  }catch(error){
    //@ts-ignore
    return rejectWithValue(error.data);
  }
});

export const fetchProducts = createAsyncThunk("fetchAllProducts", async () => {
  const response = await fetchAllProducts();
  return response.data;
});

export const completePracticeRegistration = createAsyncThunk<any, { practiceData: PracticeInfo | null , interceptorOption: InterceptorOption }>("completePracticeRegistration", async (data, { rejectWithValue }) => {
  const { practiceData, interceptorOption } = data
  try{
   const response = await completeRegistration(practiceData, interceptorOption);
   return response.data;
  }catch(error){
    //@ts-ignore
    return rejectWithValue(error.data);
  }
});

const practiceRegistrationSlice = createSlice({
  name: "practiceRegistration",
  initialState,
  reducers: {
    updatePracticeRegistrationInfoObject: (state: PracticeRegistrationState, action) => {
      state.practiceRegistrationInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitPracticeData.pending, (state) => {
        state.submitPracticeDataFetchStatus = "loading";
      })
      .addCase(submitPracticeData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.submitPracticeDataFetchStatus = "success";
        state.practiceRegistrationInfo = undefined;
      })
      .addCase(submitPracticeData.rejected, (state, action: any) => {
        state.submitPracticeDataFetchStatus = 'error';
        state.practiceRegistrationError = action.payload;
      })
      .addCase(fetchProducts.pending, (state) => {
        state.productsListFetchStatus = "loading";
      })
      .addCase(fetchProducts.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.productsListFetchStatus = "success";
        // state.productList = action.payload.data;
        state.productList = action.payload?.data ?? []; // CS-1984
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.productsListFetchStatus = 'error';
      });
  },
});

export const { updatePracticeRegistrationInfoObject } = practiceRegistrationSlice.actions;
export const usePracticeRegistrationSlice = () => useAppSelector((state) => state.practiceRegistrationSlice);
export default practiceRegistrationSlice.reducer;